import type { DayKey } from "./time.js";

export const AREAS = ["body", "mind", "work", "social", "home"] as const;
export type Area = (typeof AREAS)[number];

/**
 * Areas that grow: XP, levels, rest bonus. Home is upkeep (chores repeat, they are not progress):
 * it pays gold, keeps the flat tidy, counts as an active day and unlocks home furniture,
 * but shows no level and never levels up on screen.
 */
export const GROWTH_AREAS = ["body", "mind", "work", "social"] as const;
export const isGrowthArea = (a: Area): boolean => a !== "home";

export const AREA_NAMES_PL: Record<Area, string> = {
  body: "Ciało",
  mind: "Głowa",
  work: "Praca",
  social: "Relacje",
  home: "Dom",
};

export const SIZES = ["S", "M", "L"] as const;
export type Size = (typeof SIZES)[number];

export const ACTIVITY_ANIMATIONS = [
  "train",
  "read",
  "work",
  "write",
  "trash",
  "clean",
  "cook",
  "shop",
  "coffee",
  "beer",
  "walk",
  "other",
] as const;
export type ActivityAnimation = (typeof ACTIVITY_ANIMATIONS)[number];

export const MODES = ["normal", "sick", "vacation", "crunch"] as const;
export type Mode = (typeof MODES)[number];
/** Mode as seen by rules: "camp" is derived (no activity for 2+ days in normal mode). */
export type EffectiveMode = Mode | "camp";

export const MUTE_KINDS = ["short", "today", "days"] as const;
export type MuteKind = (typeof MUTE_KINDS)[number];

export const MONEY_TIERS = ["small", "medium", "large"] as const;
/** What the companion can do on screen when he says so ("też się kładę"). Outings leave through the door. */
export const COMPANION_ACTIVITIES = [
  "sleep",
  "nap",
  "read",
  "train",
  "cook",
  "eat",
  "clean",
  "work",
  "play",
  "sit",
  "celebrate",
  "walk_out",
  "shopping_out",
] as const;
export type CompanionActivity = (typeof COMPANION_ACTIVITIES)[number];
export type MoneyTier = (typeof MONEY_TIERS)[number];

export type DeviceProfile = "wall" | "pocket";

export interface Look {
  skin: number; // 0-4
  hair: number; // 0-5 (style)
  hairColor: number; // 0-7
  beard: number; // 0-3
  glasses: boolean;
  shirt: number; // index into shirt colors
}

export interface Settings {
  companionName: string;
  userName: string;
  timezone: string;
  briefFrom: string; // "HH:MM"
  briefUntil: string;
  debriefFrom: string;
  activeFrom: string;
  activeUntil: string;
  eveningAt: string; // "wieczorem" in reminders
  maxNudgesPerDay: number;
  nightFrom: string;
  nightUntil: string;
  comeHomeAfterHours: number; // 0 = off
  llmDailyLimit: number;
  /** The companion's own job: Monday–Friday he sits in the office between these hours. */
  workOn: boolean;
  workFrom: string;
  workUntil: string;
}

export interface Stats {
  xp: Record<Area, number>;
  gold: number;
  rested: number;
  lastActiveDay: DayKey | null;
  /** Highest body stage already celebrated, so a stage-up plays once. */
  bodyStage: number;
  /** Current form of Body, Mind and Relations (see form.ts); levels never drop, form may. */
  form: Record<"body" | "mind" | "social", { value: number; day: DayKey; paused: number }>;
}

export interface ModeState {
  kind: Mode;
  since: DayKey;
  until: DayKey | null; // mode ends at 04:00 of this day
}

export interface MuteState {
  kind: MuteKind;
  since: number;
  until: number; // epoch ms
}

export type QuestStatus = "open" | "done" | "dropped";

export interface Quest {
  id: string;
  title: string;
  area: Area;
  size: Size;
  status: QuestStatus;
  createdDay: DayKey;
  dueDay: DayKey;
  repeatDays: number | null;
  postponed: number;
  lastDoneDay: DayKey | null;
  closedDay: DayKey | null;
}

export type ReminderStatus = "pending" | "due" | "done";

export interface Reminder {
  id: string;
  text: string;
  at: number; // epoch ms
  status: ReminderStatus;
  createdAt: number;
  firedAt: number | null;
}

export interface Activity {
  id: string;
  at: number;
  day: DayKey;
  area: Area;
  text: string;
  size: Size;
  xp: number;
  bonus: number;
  gold: number;
  animation: ActivityAnimation;
  questId: string | null;
}

export interface Fact {
  id: string;
  text: string;
  at: number;
}

export interface ShoppingItem {
  id: string;
  text: string;
  done: boolean;
  at: number;
}

export interface OwnedItem {
  id: string; // catalog / milestone item id
  at: number;
  source: "milestone" | "shop" | "gift";
}

export interface ChatMessage {
  role: "user" | "assistant";
  text: string;
  at: number;
}

export const NUDGE_TYPES = ["chore", "quest", "challenge", "curiosity"] as const;
export type NudgeType = (typeof NUDGE_TYPES)[number];
export type PromptKind = "brief" | "debrief" | "weekly" | "nudge" | "return" | "reminder";
export type PromptOutcome = "answered" | "ignored" | "dismissed";

export interface OutcomeCounts {
  answered: number;
  ignored: number;
  dismissed: number;
}

export interface NudgeState {
  day: DayKey | null;
  count: number;
  lastAt: number; // last daily nudge
  lastPromptAt: number; // any prompt
  lastBriefDay: DayKey | null;
  lastDebriefDay: DayKey | null;
  lastWeeklyWeek: DayKey | null;
  returnPending: boolean;
  /** "daj mi spokój" after 22:00 also skips the next morning brief. */
  skipBriefDay: DayKey | null;
  /** key: `${type}:${hourBucket}` (bucket = 2h). Bounded by types x buckets. */
  stats: Record<string, OutcomeCounts>;
  /** Rotating template counter so lines don't repeat. */
  variant: number;
  lastType: NudgeType | null;
  /** What the companion asked last; used for outcome tracking and LLM context. */
  lastPrompt: LastPrompt | null;
}

export interface LastPrompt {
  kind: PromptKind;
  at: number;
  nudgeType: NudgeType | null;
  questId: string | null;
  reminderId: string | null;
}

export interface Location {
  deviceId: string | null;
  deviceName: string;
  profile: DeviceProfile;
  since: number;
}

export interface DeviceRecord {
  id: string;
  name: string;
  profile: DeviceProfile;
  lastSeen: number;
  visible: boolean;
}

export interface LlmUsage {
  day: DayKey | null;
  calls: number;
  inputTokens: number;
  outputTokens: number;
  totalCalls: number;
}

export interface GameState {
  version: 1;
  createdAt: number;
  onboarded: boolean;
  settings: Settings;
  look: Look;
  stats: Stats;
  mode: ModeState;
  mute: MuteState | null;
  quests: Quest[];
  reminders: Reminder[];
  facts: Fact[];
  shopping: ShoppingItem[];
  inventory: OwnedItem[];
  ratings: Record<DayKey, number>;
  nudge: NudgeState;
  /** Activities of the last days only (full history lives in storage). */
  recent: Activity[];
  messages: ChatMessage[];
  location: Location;
  devices: DeviceRecord[];
  llm: LlmUsage;
  seq: number; // id counter
}

/** Limits keep in-memory collections bounded (SPEC 8). */
export const LIMITS = {
  facts: 50,
  shopping: 40,
  messages: 12,
  recentDays: 8,
  recentMax: 300,
  closedQuests: 30,
  doneReminders: 20,
  ratings: 26,
  openQuests: 60,
  pendingReminders: 60,
  textLen: 120,
  devices: 10,
} as const;
